"use strict";
/**
 * The legacy integer-dollar engine, preserved bit-for-bit.
 *
 * Bookings created before the decimal cutover are priced here forever, so this
 * file must keep producing exactly the numbers it produces today — rounding
 * quirks included. Every oddity below is deliberate:
 *
 *   - `parseInt(cleaning_type)` truncates a decimal base price;
 *   - `Math.round` wraps the *whole* frequency expression, so the subtotal is
 *     quantised to whole dollars before the flexible, promo and add-on steps;
 *   - tax is rounded to the nearest dollar, not the nearest cent.
 *
 * Do not "fix" anything here. Fixing it changes the price of a booking that has
 * already been sold. The corrected arithmetic lives in ./decimal.ts.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLegacyTotalPrice = exports.getLegacyBreakdown = exports.getLegacyAppointmentValue = exports.getLegacyTax = exports.getLegacyAddOnsCost = void 0;
const rates_1 = require("./rates");
const dollars = (cents) => cents / 100;
/** Add-on cost in whole dollars, reproducing `getAddOnsCost` exactly. */
function getLegacyAddOnsCost(cleaningCostDollars, addOns, createdAt) {
    const card = (0, rates_1.getRateCard)(createdAt);
    const moveInOut = dollars(card.cleaningTypes.move_in_out);
    let cost = 0;
    for (const addOn of addOns || []) {
        const name = addOn.toLowerCase();
        if (name.includes("organizing"))
            cost += dollars(rates_1.ADD_ON_COST_CENTS.organizing);
        else if (name.includes("laundry"))
            cost += dollars(rates_1.ADD_ON_COST_CENTS.laundry);
        else if (name.includes("windows"))
            cost += dollars(rates_1.ADD_ON_COST_CENTS.windows);
        else if (name.includes("cabinet")) {
            if (cleaningCostDollars < moveInOut)
                cost += dollars(rates_1.ADD_ON_COST_CENTS.cabinets);
        }
        else if (name.trim() && cleaningCostDollars < moveInOut) {
            cost += dollars(rates_1.ADD_ON_COST_CENTS.oven);
        }
    }
    return cost;
}
exports.getLegacyAddOnsCost = getLegacyAddOnsCost;
/** Sales tax in whole dollars, reproducing `getTax` exactly. */
function getLegacyTax(subTotalDollars) {
    return Math.round((subTotalDollars * 8.875) / 100);
}
exports.getLegacyTax = getLegacyTax;
/** Room charge in whole dollars. The legacy engine bills bedrooms and bathrooms alike. */
function roomsCost(bedrooms, bathrooms) {
    return (parseInt(String(bedrooms), 10) * dollars(rates_1.ROOM_COST_CENTS) +
        Number(bathrooms) * dollars(rates_1.ROOM_COST_CENTS));
}
/** Rooms plus cleaning cost, in whole dollars, reproducing `getAppointmentValue`. */
function getLegacyAppointmentValue(bathrooms, bedrooms, cleaningType, createdAt) {
    const card = (0, rates_1.getRateCard)(createdAt);
    return roomsCost(bedrooms, bathrooms) + dollars(card.cleaningTypes[cleaningType]);
}
exports.getLegacyAppointmentValue = getLegacyAppointmentValue;
/** The legacy engine's own intermediate values. See {@link LegacyBreakdown}. */
function getLegacyBreakdown(input, codeDiscountDollars = 0) {
    const cleaningCost = parseInt(input.cleaning_type, 10);
    const rooms = roomsCost(input.bedrooms, input.bathrooms);
    const base = rooms + cleaningCost;
    // `Math.round` wraps the whole expression, exactly as it always has — the
    // subtotal is quantised to whole dollars here, before the remaining steps.
    const afterFrequency = Math.round(base - ((0, rates_1.getFrequencyDiscountPercent)(input.frequency) * base) / 100);
    const frequencyDiscount = base - afterFrequency;
    const flexibleDiscount = (0, rates_1.isFlexible)(input.clean_time) ? 10 : 0;
    const addOns = getLegacyAddOnsCost(cleaningCost, input.add_ons, input.created_at);
    const subTotal = afterFrequency - flexibleDiscount - codeDiscountDollars + addOns;
    const tax = getLegacyTax(subTotal);
    return {
        base,
        rooms,
        cleaningType: cleaningCost,
        frequencyDiscount,
        flexibleDiscount,
        promoDiscount: codeDiscountDollars,
        addOns,
        subTotal,
        tax,
        total: subTotal + tax,
    };
}
exports.getLegacyBreakdown = getLegacyBreakdown;
/**
 * The legacy total, in whole dollars. Byte-for-byte equivalent to
 * `apps/api/src/lib/pricing.ts#getTotalPrice`.
 *
 * Delegates to {@link getLegacyBreakdown} so the total and the lines shown
 * beside it cannot drift apart — the operation order there is unchanged from
 * the original implementation of this function.
 */
function getLegacyTotalPrice(input, codeDiscountDollars = 0) {
    return getLegacyBreakdown(input, codeDiscountDollars).total;
}
exports.getLegacyTotalPrice = getLegacyTotalPrice;
//# sourceMappingURL=legacy.js.map